import type {
  AncestralId,
  BuildingId,
  GameSettings,
  GameState,
  JobId,
  OfflineReport,
  TaleEntry,
  TechId,
} from '../engine';
import {
  advance,
  affordableBuildingCount,
  assignWorkers,
  advanceAge,
  buyAncestral,
  buyBuilding,
  buyTech,
  clearAssignments,
  collectModifiers,
  computeProduction,
  createInitialState,
  clearStorage,
  exportSave,
  importSave,
  loadFromStorage,
  manualGather,
  passTheFire,
  saveToStorage,
  setJobWorkers,
  simulateOffline,
} from '../engine';
import type { Modifiers } from '../engine';
import type { ProductionResult } from '../engine/production';

/** Simulation steps per second. */
const TICK_HZ = 10;
const TICK_MS = 1000 / TICK_HZ;
/** How often the game is written to storage. */
const AUTOSAVE_MS = 10_000;
/** Play-time seconds a tale toast stays on screen. */
const TOAST_SECONDS = 14;

export interface Snapshot {
  state: GameState;
  modifiers: Modifiers;
  production: ProductionResult;
  /** Bumped every tick so React re-renders off a cheap scalar. */
  revision: number;
}

type Listener = () => void;

function computeSnapshot(state: GameState, revision: number): Snapshot {
  const modifiers = collectModifiers(state);
  return {
    state,
    modifiers,
    production: computeProduction(state, modifiers, 1 / TICK_HZ),
    revision,
  };
}

class GameStore {
  private state: GameState;
  private snapshot: Snapshot;
  private revision = 0;
  private listeners = new Set<Listener>();
  private timer: ReturnType<typeof setInterval> | null = null;
  private lastFrameMs = Date.now();
  private sinceSaveMs = 0;

  /** Set once on boot when the player was away long enough to matter. */
  offlineReport: OfflineReport | null = null;
  /** Tales that fired since the UI last cleared them, newest first. */
  recentTales: TaleEntry[] = [];

  private storage: Storage | null;

  constructor(storage: Storage | null) {
    this.storage = storage;
    const loaded = storage ? loadFromStorage(storage) : null;
    this.state = loaded ?? createInitialState();
    if (loaded) this.offlineReport = simulateOffline(this.state);
    else this.state.lastTick = Date.now();
    this.snapshot = computeSnapshot(this.state, this.revision);
  }

  // --- React binding -------------------------------------------------------

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getSnapshot = (): Snapshot => this.snapshot;

  private publish(): void {
    this.revision += 1;
    this.snapshot = computeSnapshot(this.state, this.revision);
    for (const listener of this.listeners) listener();
  }

  // --- Loop ----------------------------------------------------------------

  start(): void {
    if (this.timer !== null) return;
    this.lastFrameMs = Date.now();
    this.timer = setInterval(() => this.frame(), TICK_MS);
  }

  stop(): void {
    if (this.timer === null) return;
    clearInterval(this.timer);
    this.timer = null;
    this.save();
  }

  private frame(): void {
    const now = Date.now();
    const elapsedMs = now - this.lastFrameMs;
    this.lastFrameMs = now;
    if (elapsedMs <= 0) return;

    const outcome = advance(this.state, elapsedMs / 1000);
    this.state.lastTick = now;

    if (outcome.talesFired.length > 0) {
      this.recentTales = [...outcome.talesFired.reverse(), ...this.recentTales].slice(0, 5);
    }
    if (this.recentTales.length > 0) {
      const cutoff = this.state.stats.totalPlaySeconds - TOAST_SECONDS;
      this.recentTales = this.recentTales.filter((tale) => tale.at >= cutoff);
    }

    this.sinceSaveMs += elapsedMs;
    if (this.sinceSaveMs >= AUTOSAVE_MS) {
      this.sinceSaveMs = 0;
      this.save();
    }

    this.publish();
  }

  // --- Persistence ---------------------------------------------------------

  save(): void {
    if (!this.storage) return;
    this.state.lastTick = Date.now();
    saveToStorage(this.storage, this.state);
  }

  exportSave(): string {
    return exportSave(this.state);
  }

  importSave(text: string): boolean {
    const imported = importSave(text);
    if (!imported) return false;
    this.state = imported;
    this.state.lastTick = Date.now();
    this.recentTales = [];
    this.offlineReport = null;
    this.save();
    this.publish();
    return true;
  }

  hardReset(): void {
    if (this.storage) clearStorage(this.storage);
    this.state = createInitialState();
    this.recentTales = [];
    this.offlineReport = null;
    this.publish();
  }

  // --- Player actions ------------------------------------------------------

  /** Every action runs through here so a UI click always repaints. */
  private act<T>(mutate: (state: GameState) => T): T {
    const outcome = mutate(this.state);
    this.publish();
    return outcome;
  }

  assign(jobId: JobId, delta: number): void {
    this.act((state) => assignWorkers(state, jobId, delta));
  }

  setJob(jobId: JobId, count: number): void {
    this.act((state) => setJobWorkers(state, jobId, count));
  }

  clearJobs(): void {
    this.act((state) => clearAssignments(state));
  }

  build(buildingId: BuildingId, quantity: number): number {
    return this.act((state) => buyBuilding(state, buildingId, quantity));
  }

  buildMax(buildingId: BuildingId): number {
    return this.act((state) =>
      buyBuilding(state, buildingId, affordableBuildingCount(state, buildingId)),
    );
  }

  research(techId: TechId): boolean {
    return this.act((state) => buyTech(state, techId));
  }

  remember(nodeId: AncestralId): boolean {
    return this.act((state) => buyAncestral(state, nodeId));
  }

  gather(): void {
    this.act((state) => manualGather(state));
  }

  passFire(): number {
    const gained = this.act((state) => passTheFire(state));
    if (gained > 0) {
      this.recentTales = [];
      this.save();
    }
    return gained;
  }

  advanceAge(): number {
    const outcome = this.act((state) => advanceAge(state));
    if (outcome.knowledge > 0) {
      this.recentTales = [];
      this.save();
    }
    return outcome.knowledge;
  }

  updateSettings(patch: Partial<GameSettings>): void {
    this.act((state) => {
      state.settings = { ...state.settings, ...patch };
    });
    this.save();
  }

  /** Dev escape hatch: the live state, plus a repaint. Mutate at your own risk. */
  get live(): GameState {
    return this.state;
  }

  refresh(): void {
    this.publish();
  }

  dismissOfflineReport(): void {
    this.offlineReport = null;
    this.publish();
  }

  dismissTale(taleAt: number): void {
    this.recentTales = this.recentTales.filter((tale) => tale.at !== taleAt);
    this.publish();
  }
}

function browserStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    // Blocked by privacy settings — play without saving rather than crash.
    return null;
  }
}

export const gameStore = new GameStore(browserStorage());
export type { GameStore };

// Dev-only handle so the running game can be poked from the console. Stripped
// from production builds by the bundler.
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as unknown as { emberline: GameStore }).emberline = gameStore;
}
